
import { isLabelContentType, LabelContentItem, setOrDeleteLabel, } from '@tableau/data-catalog-shared';
import { restApiService } from '@tableau/vizportalclient-common-components';
import { call, put } from 'redux-saga/effects';

import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { Messages } from '../../../codegen/Localize';
import { SetSensitiveLabelAction } from '../../actions/labels/SetSensitiveLabelAction';
import { defaultInitialState, DialogState, getActionDialogOptions } from '../../components/labels/LabelDialog';

export type SensitiveLabelDialogState = {
    readonly luid?: string;
    readonly note: string;
    readonly showSensitiveLabel: boolean;
}

export type SenstiveLabelDialogBodyProps = {
    readonly contentItem: LabelContentItem;
    readonly loading?: boolean;
}

export function* handleSetSensitiveLabel(action: SetSensitiveLabelAction) {
    try {
        yield call(openSenstiveLabelDialog, action);

        const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
        if (!maybeReloaded) {
          yield put(requestSpaceContents());
        }
    } catch(e) {
        yield put(requestFailed(e, Messages.errorUnknown()));
    }
}

export function openSenstiveLabelDialogCatalog({ contentItem, action, onSubmit }: {
    readonly contentItem: LabelContentItem;
    readonly action?: SetSensitiveLabelAction;
    readonly onSubmit?: () => void;
  }): Promise<unknown>  {

    const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
    const {makeJsonRestApiRequest} = restApiService({siteLuid});

    // eslint-disable-next-line no-empty-pattern
    const doAction: (formData: DialogState) => Promise<unknown> = ({ luid, isActive, message, type }) => {
        return setOrDeleteLabel({
            category: 'sensitivity',
            makeJsonRestApiRequest,
            contentItems: [contentItem],
            labelContent: {
              active: isActive,
              elevated: false,
              message: message,
              value: type,
            }
          }).then(async _ => {
            ToasterService.instance.pop({
              type: ToastType.Info,
              text: Messages.labelUpdateSuccess({
                LABEL_CATEGORY: Messages.labelCategorySensitivity(),
                CONTENT_TYPE: contentItem.contentType,
                CONTENT_NAME: contentItem.contentName,
              }),
            });
            if (onSubmit) {
              ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
              onSubmit();
            }
          }).catch(_ => ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.dataQuality_setError({ CONTENT_NAME: contentItem.contentName })
          }));
    };

    const initialBodyProps: SenstiveLabelDialogBodyProps = { contentItem };
    const modalInstance = openReactActionDialog(getActionDialogOptions({
      initialBodyProps,
      initialFormData: defaultInitialState('sensitivity'),
      doAction,
      labelCategory: 'sensitivity',
    }));

    return modalInstance.result;
}

export function openSenstiveLabelDialog(action: SetSensitiveLabelAction): PromiseLike<unknown> {
    const { luid: contentId, name: contentName, contentType } = action.item;
    if (!isLabelContentType(contentType)) {
      throw new Error(`${action.item.contentType} Sensitive Label cannot be put on this content type`);
    }

    const contentItem: LabelContentItem = { contentId, contentName, contentType };

    return openSenstiveLabelDialogCatalog({ contentItem, action });
}